import { useState, useEffect } from "react";
import AddUserModal from "./AddUserModal";
import EditUserModal from "./EditUserModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import useAdminData from "../hooks/useAdminData";
import DeleteIcon from "@mui/icons-material/Delete";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import { useTheme } from "../context/ThemeContext"; // 🔥 ThemeContext importálása
import API_BASE_URL from "../config";
import { useNavigate } from "react-router-dom";

const UserList = () => {
  const { users, fetchUsers } = useAdminData();
  const { darkMode } = useTheme(); // 🔥 Dark mode állapot lekérése
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const navigate = useNavigate();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const regionNames = {
    0: "Oradea",
    2000: "Arad",
    4000: "Leș",
    8000: "Leș 2"
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDelete = async (id, name) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/users/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const responseText = await response.text();
      console.log("Szerver válasza:", responseText);

      setIsDeleteModalOpen(false);

      if (!response.ok) {
        let errorMessage = `Eroare la ștergerea utilizatorului ${name}: ${responseText}`;

        if (
          responseText.includes("Cannot delete or update a parent row") ||
          responseText.includes("FOREIGN KEY constraint fails")
        ) {
          errorMessage = `Utilizatorul ${name} nu poate fi șters deoarece are clienți asociați.`;
        }

        setDeleteError(errorMessage);
        setTimeout(() => setDeleteError(null), 2000);
        return;
      }

      fetchUsers();
      setDeleteSuccessMessage(`Utilizatorul ${name} a fost șters cu succes!`);
      setTimeout(() => setDeleteSuccessMessage(null), 3000);
    } catch (err) {
      console.error("Eroare la ștergere:", err.message);
      setDeleteError(`Eroare la ștergerea utilizatorului ${name}.`);
      setTimeout(() => setDeleteError(null), 2000);
    }
  };

  return (
    <div
      className={`min-h-screen p-6 ${darkMode ? "bg-gray-900" : "bg-gray-200"}`}
    >
      <div className="flex justify-between items-center mb-4">
        <h2
          className={`text-2xl font-semibold ${darkMode ? "text-white" : "text-black"
            }`}
        >
          Lista utilizatorilor
        </h2>
        <button
          onClick={() => setIsModalOpen(true)}
          className="px-4 py-2 rounded-3xl transition border-2"
          style={{
            borderColor: darkMode ? "#90caf9" : "#0056B3",
            color: darkMode ? "#90caf9" : "#ffffff",
            backgroundColor: darkMode ? "transparent" : "#0056B3",
            fontWeight: "bold",
            transition: "all 0.3s ease-in-out",
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = darkMode ? "#90caf9" : "#003B80";
            e.target.style.backgroundColor = darkMode
              ? "rgba(144, 202, 249, 0.1)"
              : "rgba(0, 86, 179, 0.15)";
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = darkMode ? "#90caf9" : "#0056B3";
            e.target.style.backgroundColor = darkMode
              ? "transparent"
              : "#0056B3";
          }}
        >
          + Adaugă utilizator
        </button>
      </div>

      {deleteSuccessMessage && (
        <div className="bg-green-600 text-white text-center p-3 rounded-lg mb-4">
          {deleteSuccessMessage}
        </div>
      )}

      {deleteError && (
        <div className="bg-red-600 text-white text-center p-3 rounded-lg mb-4">
          {deleteError}
        </div>
      )}

      <div className="overflow-x-auto">
        <table
          className={`w-full border ${darkMode
            ? "border-gray-700 bg-gray-900 text-gray-300"
            : "border-gray-400 bg-white text-gray-800 shadow-lg"
            }`}
        >
          <thead>
            <tr
              className={`${darkMode
                ? "bg-gray-700 text-white"
                : "bg-gray-300 text-gray-900"
                }`}
            >
              <th className="border p-2">Nr</th>
              <th className="border p-2">Nume</th>
              <th className="border p-2">Email</th>
              <th className="border p-2">Rol</th>
              <th className="border p-2">Regiune</th>
              <th className="border p-2">Tip clienți</th>
              <th className="border p-2">Telefon</th>
              <th className="border p-2">Creat</th>
              <th className="border p-2">Actualizat</th>
              <th className="border p-2">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {users.length > 0 ? (
              users.map((user, index) => (
                <tr
                  key={user.id}
                  className={`border ${darkMode
                    ? index % 2 === 0
                      ? "bg-gray-800 hover:bg-gray-700" // Páros sor: világosabb szürke
                      : "bg-gray-900 hover:bg-gray-700" // Páratlan sor: sötétebb szürke
                    : index % 2 === 0
                      ? "bg-gray-50 hover:bg-gray-200" // Világos mód: világosabb
                      : "bg-white hover:bg-gray-200" // Világos mód: fehér
                    } transition text-center`}
                >
                  <td className="border p-2">{user.id}</td>
                  <td
                    className="border p-2 cursor-pointer underline text-blue-500 hover:text-blue-700"
                    onClick={() => navigate(`/dashboard/agent/${user.id}`)}
                  >
                    {user.name}
                  </td>
                  <td className="border p-2">{user.email}</td>
                  <td className="border p-2">{user.role}</td>
                  <td className="border p-2">{regionNames[user.region] || "N/A"}</td>
                  <td className="border p-2">
                    {user.role === "Admin" ||
                      (user.role === "Agent" && user.canAccessMarmurist)
                      ? "Finali & Marmiste"
                      : "Finali"}
                  </td>

                  <td className="border p-2">{user.phone || "N/A"}</td>
                  <td className="border p-2">
                    {new Date(user.createdAt).toISOString().split("T")[0]}
                  </td>
                  <td className="border p-2">
                    {new Date(user.updatedAt).toISOString().split("T")[0]}
                  </td>
                  <td className="border p-2 flex justify-center gap-2">
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      sx={(theme) => ({
                        borderColor:
                          theme.palette.mode === "dark" ? "#90caf9" : "#0056B3",
                        color:
                          theme.palette.mode === "dark" ? "#90caf9" : "#0056B3",
                        // fontWeight:
                        //   theme.palette.mode === "dark" ? "normal" : "bold",
                        "&:hover": {
                          borderColor:
                            theme.palette.mode === "dark"
                              ? "#90caf9"
                              : "#003B80",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(144, 202, 249, 0.1)"
                              : "rgba(0, 86, 179, 0.15)",
                        },
                      })}
                      onClick={() => {
                        setSelectedUser(user);
                        setIsEditModalOpen(true);
                      }}
                    >
                      Editare
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                      sx={{
                        borderColor: "#DC3545",
                        color: "#DC3545",
                        "&:hover": {
                          borderColor: "#A71D2A",
                          backgroundColor: "rgba(220, 53, 69, 0.1)",
                        },
                      }}
                      onClick={() => {
                        setUserToDelete(user);
                        setIsDeleteModalOpen(true);
                      }}
                    >
                      Ștergere
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center p-4 text-gray-400">
                  Nu există utilizatori în sistem.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={() =>
          userToDelete &&
          handleDelete(
            userToDelete.id,
            userToDelete.name || "Utilizator necunoscut"
          )
        }
        user={userToDelete}
      />

      <AddUserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUserAdded={fetchUsers}
      />
      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        user={selectedUser}
        onUserUpdated={fetchUsers}
      />
    </div>
  );
};

export default UserList;
